const CONTENT = {
  tokens: {
    eyebrow: "Crypto token leaderboard",
    title: "Proof moves the board.",
    intro: "FLIP ranks crypto tokens by verified supporter contributions, not votes, likes or paid placements. Every backing action is recorded on-chain and reflected on the live board, so the order shows where supporters are putting real weight behind their bags.",
    sections: [
      ["How token rankings work", "Choose a listed token, connect a wallet and back it with an accepted stablecoin. The contribution adds to that token’s verified total. When its total passes another token, the board updates and the rank changes. The all-time, 24-hour and 7-day views show both sustained support and fresh momentum."],
      ["What makes FLIP different", "Most crypto rankings sort by market cap, price or trading volume. FLIP measures direct community backing instead. It does not promise a return or predict which token will rise. It gives supporters a transparent way to show conviction and lets anyone verify the activity behind each position."],
    ],
    faq: [
      ["Can a token buy the top rank?", "A rank only moves when verified contributions raise its total above the token ahead of it."],
      ["How fast does the leaderboard update?", "The board updates in real time after a contribution is confirmed and processed."],
    ],
  },
  callers: {
    eyebrow: "Crypto callers leaderboard",
    title: "Back the voice. Verify the support.",
    intro: "The FLIP callers board ranks crypto commentators, researchers and community voices by verified supporter backing. It replaces vague follower counts and popularity polls with a public record of contributions that can be checked on-chain.",
    sections: [
      ["How callers earn rank", "Supporters choose a listed caller and contribute with an accepted stablecoin. Each confirmed contribution increases that caller’s total. When the new total passes the next position, the ranking changes. Use the all-time view for durable support and the shorter windows to see which voices are gaining momentum now."],
      ["A leaderboard, not an endorsement", "A high rank shows that supporters have backed a caller on FLIP. It does not certify their accuracy, guarantee their calls or replace your own research. The board makes support visible; it does not turn attention into financial advice. Review the public activity and decide who has earned your backing."],
    ],
    faq: [
      ["Who can be nominated?", "Eligible crypto callers and community voices can be nominated, then activated through the first qualifying contribution."],
      ["Are rankings paid placements?", "No. Positions follow verified backing totals rather than a hidden sponsorship order."],
    ],
  },
  backers: {
    eyebrow: "On-chain crypto backers",
    title: "See who moves the rankings.",
    intro: "The Top Backers page shows the wallets contributing the most support across FLIP. Rankings are based on the total verified USD value contributed to tokens and callers, with time filters for all-time backing and recent activity.",
    sections: [
      ["What the backer ranking measures", "Every confirmed contribution is assigned to the wallet that made it. FLIP adds the verified value across supported tokens and callers, then orders wallets by that total. Contribution counts show how often a wallet has backed an entry, while the value column shows the combined weight behind those actions."],
      ["Why wallet-level transparency matters", "Public backing makes the leaderboard easier to audit. Visitors can distinguish broad community support from a small number of large moves, follow a wallet’s contribution history and see which tokens or callers received that support. Wallet labels are shortened unless a public name is available; FLIP does not claim to identify the person behind an address."],
    ],
    faq: [
      ["Can I view a backer’s history?", "Yes. Open a wallet row to see the public FLIP contributions associated with that address."],
      ["What do the time filters change?", "They recalculate the ranking using contributions made inside the selected 24-hour, 7-day or 30-day window."],
    ],
  },
  stats: {
    eyebrow: "Live crypto leaderboard data",
    title: "Read the board beyond the ranks.",
    intro: "FLIP Stats turns live on-chain backing into a compact view of platform activity. It shows the verified value behind the board, recent contribution volume, active participants and the rank changes created by that support.",
    sections: [
      ["What the live statistics show", "Total backed measures the cumulative verified value contributed through FLIP. The 24-hour volume and contribution counts show current activity. Active entities tracks the tokens and callers participating on the board, while flips record moments when new backing changes a position."],
      ["How to interpret a rank war", "A flip means one entry has passed another after a confirmed contribution. Several flips in a short period can signal an active competition for position, but the data is descriptive rather than predictive. It shows what supporters did on FLIP; it does not forecast token price, caller accuracy or future returns."],
    ],
    faq: [
      ["How often do the numbers update?", "The page refreshes in real time as confirmed leaderboard activity reaches FLIP."],
      ["Is every dollar visible on-chain?", "FLIP derives leaderboard totals from accepted, verifiable contributions and links activity to the relevant public entries."],
    ],
  },
};

export default function SeoContent({ variant }) {
  const content = CONTENT[variant];
  if (!content) return null;

  return (
    <section className="mx-auto max-w-6xl px-4 pb-24" aria-labelledby={`${variant}-guide-title`}>
      <div className="rounded-3xl border border-white/10 bg-white/[0.02] px-5 py-8 sm:px-8 sm:py-10">
        <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-gold">{content.eyebrow}</div>
        <h2 id={`${variant}-guide-title`} className="mt-2 font-display text-2xl font-black uppercase tracking-tight sm:text-3xl">{content.title}</h2>
        <p className="mt-4 max-w-3xl text-sm leading-7 text-zinc-400">{content.intro}</p>

        <div className="mt-8 grid gap-6 md:grid-cols-2">
          {content.sections.map(([heading, body]) => (
            <div key={heading}>
              <h3 className="font-display text-base font-black uppercase tracking-wide text-zinc-100">{heading}</h3>
              <p className="mt-2 text-sm leading-7 text-zinc-400">{body}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 border-t border-white/10 pt-6">
          <h3 className="font-display text-sm font-black uppercase tracking-[0.16em] text-zinc-200">Quick answers</h3>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            {content.faq.map(([question, answer]) => (
              <div key={question}>
                <h4 className="text-sm font-bold text-zinc-200">{question}</h4>
                <p className="mt-1 text-sm leading-6 text-zinc-500">{answer}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
